import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicUser } from '@tappa/shared';
import { ApiError, api } from './api.js';

interface AuthState {
  readonly user: PublicUser | null;
  readonly isLoading: boolean;
  /** true se la sessione non e' verificabile perche' siamo offline. */
  readonly isOffline: boolean;
  readonly login: (email: string, password: string) => Promise<void>;
  readonly register: (input: {
    email: string;
    password: string;
    displayName: string;
  }) => Promise<void>;
  readonly logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const { user } = await api.auth.me();
        return user;
      } catch (error) {
        // 401 non e' un guasto: significa "non autenticato". Qualunque altro
        // errore risale, cosi' non confondiamo "offline" con "non loggato".
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) => api.auth.login(input),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(['auth', 'me'], user);
      void queryClient.invalidateQueries({ queryKey: ['trips'] });
    },
  });

  const registerMutation = useMutation({
    mutationFn: (input: { email: string; password: string; displayName: string }) =>
      api.auth.register(input),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(['auth', 'me'], user);
    },
  });

  const logoutMutation = useMutation({
    mutationFn: () => api.auth.logout(),
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
    },
  });

  const value = useMemo<AuthState>(
    () => ({
      user: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isOffline: meQuery.error instanceof ApiError && meQuery.error.isOffline,
      login: async (email, password) => {
        await loginMutation.mutateAsync({ email, password });
      },
      register: async (input) => {
        await registerMutation.mutateAsync(input);
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
    }),
    [meQuery.data, meQuery.isLoading, meQuery.error, loginMutation, registerMutation, logoutMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) throw new Error('useAuth va usato dentro AuthProvider');
  return context;
}
